import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAgentTones,
  createAgentTone,
  updateAgentTone,
  deleteAgentTone,
  setDefaultAgentTone,
  type AgentTone,
  type CreateAgentToneRequest,
} from '../api/agent-tones.api';
import { getData } from '../../../utils/functional/fp';
import { useAuth } from '../../hooks/useAuth';
import { useActor } from '../../hooks/useActor';

const QUERY_KEY = ['agent-tones'] as const;
export const EMPTY_AGENT_TONES: AgentTone[] = [];

// Module-level stable references — prevent useAgentTones callers from
// triggering unnecessary config rememos when query state updates.
export function selectAgentTones(tones: AgentTone[] | undefined): AgentTone[] {
  return tones ?? EMPTY_AGENT_TONES;
}
export function selectNoTones(): AgentTone[] {
  return EMPTY_AGENT_TONES;
}

export function useAgentTones() {
  const { isAuthenticated } = useAuth();
  // Agent tones are Cowbell-internal: every ds-docmessage agent-tones endpoint
  // is @PreAuthorize'd to the Cowbell account, so firing the query for other
  // personas (carrier/broker/policyholder sessions, incl. in-app jumps) only
  // produces a guaranteed 403 on every chat mount (OMNI-589).
  //
  // Two gates are needed: `enabled` stops the doomed fetch, and the select
  // swap hides tones a prior Cowbell session already cached under this key —
  // disabling a query does NOT evict its cache, so after an in-app jump the
  // observer would otherwise keep serving the Cowbell tones list. The cache
  // itself is left intact so a jump BACK to Cowbell restores tones instantly.
  const { isCowbell } = useActor();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      getAgentTones({})
        .then(getData)
        .then((r) => r.content),
    enabled: isAuthenticated && isCowbell,
    staleTime: 5 * 60 * 1000,
    select: isCowbell ? selectAgentTones : selectNoTones,
  });
}

export function useCreateAgentTone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentToneRequest) =>
      createAgentTone({ data }).then(getData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateAgentTone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateAgentToneRequest }) =>
      updateAgentTone(id, { data }).then(getData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteAgentTone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAgentTone(id).then(getData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useSetDefaultAgentTone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setDefaultAgentTone(id).then(getData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
